import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import type { VideoJsPlayer } from '@uncinc/uncinc-react-kitchen-sink/dist/components/MediaPlayer/types';

import { SCREEN_MD } from '../../../config/constants';
import { ButtonWrapper, ParagraphsRenderer, VariableRatiosPicture } from '../../../components';
import getPrefersReducedMotion from '../../../helpers/getPrefersReducedMotion';
import type { NodeHomePage as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface PlayButtonProps {
  buttonClass: string;
  videoRef: VideoJsPlayer;
  isVideoPaused: boolean;
  setVideoPaused: (arg0: boolean) => void;
}

const PlayButton: React.FC<PlayButtonProps> = ({
  buttonClass,
  videoRef,
  isVideoPaused,
  setVideoPaused,
}) => {
  const { t } = useTranslation();

  return (
    <ButtonWrapper
      className={`NodeHomePage__hero__action-buttons__play-button ${buttonClass}`}
      inverted
      type="secondary"
      variant="icon-only"
      iconName={isVideoPaused ? 'play_arrow' : 'pause'}
      aria-label={t(`components.media_player.${isVideoPaused ? 'play_video' : 'pause_video'}`)}
      aria-pressed={!isVideoPaused}
      onClick={() => {
        if (!videoRef.current) {
          return;
        }

        if (videoRef.current.paused) {
          videoRef.current.play();
          setVideoPaused(false);
          return;
        }

        videoRef.current.pause();
        setVideoPaused(true);
      }}
    />
  );
};
interface HomePageProps {
  entity: ImmutableEntity<Type>;
  className?: string;
}

const prefersReducedMotion = getPrefersReducedMotion();

const NodeHomePage: React.FC<HomePageProps> = ({
  entity,
  className,
}) => {
  const homePage = entity.toJS();
  const heroTitle = homePage.title[0].value;
  const heroImage = homePage.field_media_item[0];
  const heroVideoMobile = homePage.field_hero_video_mobile[0];
  const heroVideoDesktop = homePage.field_hero_video_desktop[0];

  const link = homePage.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const linkSecondary = homePage.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const classes = classNames('Node', 'NodeHomePage', className);

  const { width } = useWindowDimensions();
  const windowIsMobile = useMemo(() => width < SCREEN_MD, [width]);

  const mediaPlayerClassName = classNames({
    NodeHomePage__hero__media__videoMobile: windowIsMobile,
    NodeHomePage__hero__media__videoDesktop: !windowIsMobile,
  });

  const animationRef = useRef<HTMLDivElement>(null);

  const videoRef = useRef<VideoJsPlayer | null>(null);
  const [isVideoPaused, setVideoPaused] = useState(prefersReducedMotion);
  const [hasVideoStarted, setVideoStarted] = useState(false);

  useEffect(() => {
    // Hide the Lottie when the user is scrolling.
    const hideAnimation = () => {
      if (window.scrollY > 100 && animationRef.current) {
        animationRef.current.classList.add('hideAnimation');
        window.removeEventListener('scroll', hideAnimation);
      }
    };

    window.addEventListener('scroll', hideAnimation);

    return () => window.removeEventListener('scroll', hideAnimation);
  }, []);

  // Wait until we have a browser that executes JavaScript before rendering the hero.
  // Only when we have a browser, can we determine to render the mobile or desktop video
  // by using the useWindowDimensions hook.
  //
  // Placing two elements in the DOM and setting one or the other to display: none
  // still loads the media for both elements.
  const [renderHeroVideo, setRenderHeroVideo] = useState(false);
  useEffect(() => {
    setRenderHeroVideo(true);
  }, []);

  useEffect(() => {
    let video: VideoJsPlayer;
    // Add event listener to track when the video starts playing.
    const handlePlay = () => {
      setVideoStarted(true);
    };

    if (renderHeroVideo) {
      video = videoRef.current;

      if (video) {
        video.addEventListener('play', handlePlay);
      }
    }

    return () => {
      if (video && renderHeroVideo) {
        video.removeEventListener('play', handlePlay);
      }
    };
    // Trigger this useEffect only áfter renderHeroVideo has been set to true.
  }, [renderHeroVideo]);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeHomePage__hero">
        <div className="NodeHomePage__hero__media">
          {(!heroVideoMobile || !heroVideoDesktop) && heroImage && (
            <VariableRatiosPicture
              image={heroImage}
              aspectRatios={['9_16', '3_4', '4_3', '16_9', '3_1']}
              cropSizes={['sm', 'md', 'lg', 'lg', 'xl']}
            />
          )}
          {heroVideoMobile && heroVideoDesktop && renderHeroVideo && (
            <div className={mediaPlayerClassName}>
              <div
                className={classNames(
                  'MediaPlayer',
                  'video-js',
                  'vjs-default-skin',
                  'vjs-big-play-centered',
                  'vjs-fluid',
                  'vjs_video_3-dimensions',
                  'vjs-controls-disabled',
                  'vjs-workinghover',
                  'vjs-v7',
                  'vjs-user-inactive',
                  { 'vjs-playing': hasVideoStarted && !isVideoPaused },
                  { 'vjs-paused': isVideoPaused },
                  { 'vjs-has-started': hasVideoStarted },
                )}
                tabIndex={-1}
                role="presentation"
              >
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  className="vjs-tech"
                  autoPlay={!prefersReducedMotion}
                  muted
                  loop
                  playsInline
                  controls={false}
                  ref={videoRef}
                  src={windowIsMobile ? heroVideoMobile?.source : heroVideoDesktop?.source}
                />
              </div>
            </div>
          )}
        </div>
        <div className="NodeHomePage__hero__containerWrapper">
          {heroVideoMobile && heroVideoDesktop && (
            <Container className="pauseButtonContainer">
              <div className="row">
                <div className="col-12">
                  <PlayButton
                    buttonClass="playButtonMobile"
                    videoRef={videoRef}
                    isVideoPaused={isVideoPaused}
                    setVideoPaused={setVideoPaused}
                  />
                </div>
              </div>
            </Container>
          )}
          <Container>
            <div className="row">
              <div className="col-12 col-md-9 col-lg-9 col-xl-7">
                <div className="NodeHomePage__hero__text-wrapper">
                  <h1 itemProp="title" className="NodeHomePage__hero__title asH1-XL">{heroTitle}</h1>
                </div>
              </div>

              {(link || linkSecondary || (heroVideoMobile && heroVideoDesktop)) && (
                <div className="col-12">
                  <div className="NodeHomePage__hero__action-buttons">
                    {(link || linkSecondary) && (
                      <div className="NodeHomePage__hero__links">
                        {link && (
                          <ButtonWrapper
                            inverted
                            type="secondary"
                            href={linkUrl}
                          >
                            {linkTitle}
                          </ButtonWrapper>
                        )}
                        {linkSecondary && (
                          <ButtonWrapper
                            inverted
                            type="secondary"
                            href={linkSecondaryUrl}
                          >
                            {linkSecondaryTitle}
                          </ButtonWrapper>
                        )}
                      </div>
                    )}

                    {heroVideoMobile && heroVideoDesktop && (
                      <PlayButton
                        buttonClass="playButtonDesktop"
                        videoRef={videoRef}
                        isVideoPaused={isVideoPaused}
                        setVideoPaused={setVideoPaused}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </Container>
        </div>
        <div ref={animationRef} className="NodeHomePage__hero__animation">
          <div className="box">
            <div className="line2" />
            <div className="line1" />
            <div className="line3" />
          </div>
        </div>
      </div>
      <ParagraphsRenderer className="NodeHomePage__paragraphs" paragraphs={entity.get('field_paragraphs')} isWide />
    </article>
  );
};

export default NodeHomePage;
